import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Trash2, Upload, Search, Download } from "lucide-react";
import BENumericInput from "@/components/quote/BENumericInput";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole } from "@/lib/roles";

const UNITS = ["m²", "ml", "m³", "unité", "forfait", "kWc", "kWh", "kVA", "h", "j", "kg", "t"];

const EXAMPLE_TSV = `libellé\tdescription pro\tunité\tprix\ttva
Démoussage\tDémoussage et traitement hydrofuge de la toiture\tm²\t18.50\t21
Remplacement tuiles\tRemplacement de tuiles endommagées, dépose/repose comprise\tunité\t8.20\t21
Panneau 500 Wc\tFourniture et pose de module photovoltaïque 500 Wc\tunité\t420\t21
Onduleur hybride 5 kVA\tFourniture et pose onduleur hybride\tunité\t1650\t21
Batterie 10 kWh\tFourniture et pose batterie de stockage 10 kWh\tunité\t4200\t21
Tranchée câblage\tRéalisation tranchée et pose du câblage\tml\t35\t21
Mise à la terre\tMise à la terre installation photovoltaïque\tforfait\t180\t21
`;

export default function CatalogPage() {
  const { state } = useAuth();
  // Phase B lot correctifs (2026-09-10) — Suppression réservée à admin/commercial/secretaire.
  // Le backend renvoie 403 pour gestionnaire_chantier ; on masque le bouton
  // (doctrine RBAC : UI reflète le backend).
  const canDelete = !hasRole(state?.user, ["gestionnaire_chantier"]);
  const [items, setItems] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [editing, setEditing] = useState(null); // {id?, label, ...}
  const [importOpen, setImportOpen] = useState(false);
  const [tsvContent, setTsvContent] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [duplicatesCount, setDuplicatesCount] = useState(0);

  const refreshDuplicatesCount = async () => {
    try {
      const { data } = await api.get("/catalog/duplicates");
      setDuplicatesCount(data.total_groups || 0);
    } catch {
      setDuplicatesCount(0);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [ci, ct] = await Promise.all([api.get("/catalog"), api.get("/chantier-types")]);
      setItems(ci.data);
      setTypes(ct.data);
    } finally {
      setLoading(false);
    }
    refreshDuplicatesCount();
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter((it) => {
    if (search && !it.label.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterType !== "all" && !it.chantier_type_ids?.includes(filterType)) return false;
    return true;
  });

  const openNew = () =>
    setEditing({
      label: "",
      pro_description: "",
      unit: "unité",
      unit_price: null,
      default_vat_rate: 21,
      chantier_type_ids: [],
    });

  const saveItem = async () => {
    if (!editing.label.trim()) {
      toast.error("Libellé requis");
      return;
    }
    try {
      if (editing.id) {
        await api.patch(`/catalog/${editing.id}`, editing);
      } else {
        await api.post("/catalog", editing);
      }
      toast.success("Poste enregistré");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeItem = async (it) => {
    if (!window.confirm(`Supprimer "${it.label}" ?`)) return;
    try {
      await api.delete(`/catalog/${it.id}`);
      toast.success("Poste supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runImport = async (dryRun) => {
    setImporting(true);
    try {
      const { data } = await api.post("/catalog/import", {
        tsv_content: tsvContent,
        dry_run: dryRun,
      });
      setImportResult(data);
      if (!dryRun) {
        toast.success(`${data.created} postes importés`, {
          description: (data.skipped_duplicates || 0) > 0 ? `${data.skipped_duplicates} doublon(s) ignoré(s)` : undefined,
        });
        load();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };

  const downloadExample = () => {
    const blob = new Blob([EXAMPLE_TSV], { type: "text/tab-separated-values;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "catalogue-exemple.tsv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="catalog-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Catalogue de postes</h1>
          <p className="text-sm text-slate-400">
            Bibliothèque de prestations et prix — servira à la structuration IA de vos devis.
          </p>
        </div>
        <div className="flex gap-2">
          {canDelete && duplicatesCount > 0 && (
          <Button
            variant="outline"
            onClick={async () => {
              try {
                const { data } = await api.get("/catalog/duplicates");
                if (data.total_groups === 0) {
                  toast.info("Aucun doublon détecté");
                  setDuplicatesCount(0);
                  return;
                }
                const total = data.duplicates.reduce((s, g) => s + (g.count - 1), 0);
                if (!window.confirm(
                  `${data.total_groups} groupe(s) de doublons détecté(s) — ${total} entrée(s) seront supprimée(s).\n\n` +
                    `Le plus ancien de chaque groupe est conservé. Les lignes de devis référençant les doublons seront remappées vers l'original conservé.\n\nContinuer ?`
                )) return;
                const { data: r } = await api.post("/catalog/dedup");
                toast.success(`${r.removed} doublon(s) supprimé(s)`, {
                  description: r.remapped_lines > 0 ? `${r.remapped_lines} ligne(s) de devis remappée(s).` : undefined,
                });
                await load();
              } catch (e) {
                toast.error(formatApiError(e));
              }
            }}
            className="border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800"
            data-testid="cleanup-duplicates-btn"
          >
            <Trash2 className="mr-2 h-4 w-4" /> Nettoyer doublons ({duplicatesCount})
          </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              setImportOpen(true);
              setTsvContent("");
              setImportResult(null);
            }}
            className="border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-800"
            data-testid="import-tsv-btn"
          >
            <Upload className="mr-2 h-4 w-4" /> Importer TSV
          </Button>
          <Button
            onClick={openNew}
            className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
            data-testid="add-item-btn"
          >
            <Plus className="mr-2 h-4 w-4" /> Ajouter un poste
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un libellé…"
            className="w-64 border-slate-700 bg-slate-950/40 pl-9 text-slate-100"
            data-testid="catalog-search"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-56 border-slate-700 bg-slate-950/40" data-testid="filter-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-slate-700 bg-slate-900">
            <SelectItem value="all">Tous types de chantier</SelectItem>
            {types.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-800 bg-slate-900/40">
        <CardContent className="overflow-x-auto p-0">
          {loading ? (
            <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : filtered.length === 0 ? (
            <p className="p-6 text-sm text-slate-500">Aucun poste. Ajoutez-en ou importez un TSV.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-slate-800 text-slate-400">
                <tr>
                  <th className="p-3 text-left">Libellé</th>
                  <th className="p-3 text-left">Unité</th>
                  <th className="p-3 text-right">Prix</th>
                  <th className="p-3 text-left">TVA</th>
                  <th className="p-3 text-left">Types</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr
                    key={it.id}
                    className="cursor-pointer border-b border-slate-800/60 last:border-0 hover:bg-slate-800/30"
                    onClick={() => setEditing({ ...it })}
                    data-testid={`catalog-row-${it.label}`}
                  >
                    <td className="p-3">
                      <div className="font-medium">{it.label}</div>
                      {it.pro_description && (
                        <div className="text-xs text-slate-500">{it.pro_description}</div>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs text-slate-400">{it.unit}</td>
                    <td className="p-3 text-right font-mono">
                      {it.unit_price != null ? `${it.unit_price.toFixed(2)} €` : "—"}
                    </td>
                    <td className="p-3 text-slate-400">{it.default_vat_rate}%</td>
                    <td className="p-3 text-xs text-slate-400">
                      {(it.chantier_type_ids || [])
                        .map((id) => types.find((t) => t.id === id)?.name)
                        .filter(Boolean)
                        .join(", ") || "—"}
                    </td>
                    <td className="p-3 text-right">
                      {canDelete && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeItem(it);
                        }}
                        className="rounded p-1.5 text-slate-400 hover:bg-red-500/10 hover:text-red-400"
                        data-testid={`catalog-delete-btn-${it.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Edit / New modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Modifier le poste" : "Nouveau poste"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Libellé</Label>
                <Input
                  value={editing.label}
                  onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                  className="border-slate-700 bg-slate-950/40"
                  data-testid="edit-label"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Description pro (utilisée dans le devis)</Label>
                <Textarea
                  rows={3}
                  value={editing.pro_description}
                  onChange={(e) => setEditing({ ...editing, pro_description: e.target.value })}
                  className="border-slate-700 bg-slate-950/40"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1.5">
                  <Label>Unité</Label>
                  <Select
                    value={editing.unit}
                    onValueChange={(v) => setEditing({ ...editing, unit: v })}
                  >
                    <SelectTrigger className="border-slate-700 bg-slate-950/40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-slate-700 bg-slate-900">
                      {UNITS.includes(editing.unit) ? null : (
                        <SelectItem value={editing.unit}>{editing.unit}</SelectItem>
                      )}
                      {UNITS.map((u) => (
                        <SelectItem key={u} value={u}>
                          {u}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Prix (€)</Label>
                  <BENumericInput
                    value={editing.unit_price ?? 0}
                    onChange={(n) =>
                      setEditing({
                        ...editing,
                        unit_price: Number.isFinite(n) ? n : 0,
                      })
                    }
                    className="border-slate-700 bg-slate-950/40"
                    data-testid="catalog-unit-price-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>TVA</Label>
                  <Select
                    value={String(editing.default_vat_rate)}
                    onValueChange={(v) => setEditing({ ...editing, default_vat_rate: parseInt(v, 10) })}
                  >
                    <SelectTrigger className="border-slate-700 bg-slate-950/40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-slate-700 bg-slate-900">
                      <SelectItem value="6">6 %</SelectItem>
                      <SelectItem value="21">21 %</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {types.length > 0 && (
                <div className="space-y-1.5">
                  <Label>Types de chantier associés</Label>
                  <div className="flex flex-wrap gap-2">
                    {types.map((t) => {
                      const on = editing.chantier_type_ids?.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() =>
                            setEditing({
                              ...editing,
                              chantier_type_ids: on
                                ? editing.chantier_type_ids.filter((x) => x !== t.id)
                                : [...(editing.chantier_type_ids || []), t.id],
                            })
                          }
                          className={`rounded-full px-3 py-1 text-xs ${
                            on
                              ? "bg-[#C4553B] text-white"
                              : "border border-slate-700 bg-slate-800/60 text-slate-300"
                          }`}
                        >
                          {t.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditing(null)}
              className="border-slate-700 bg-slate-800 text-slate-200"
            >
              Annuler
            </Button>
            <Button
              onClick={saveItem}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="save-item-btn"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import TSV modal */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-100 sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Importer des postes (TSV)</DialogTitle>
            <DialogDescription className="text-slate-400">
              Collez ou éditez le contenu TSV, prévisualisez le résultat, puis confirmez l&apos;import.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>
                Colonnes attendues : <code>libellé, description, unité, prix, tva</code> —
                séparateur TAB
              </span>
              <button
                onClick={downloadExample}
                className="inline-flex items-center gap-1 text-[#C4553B] hover:underline"
                data-testid="download-example"
              >
                <Download className="h-3 w-3" /> Exemple
              </button>
            </div>
            <Textarea
              rows={10}
              value={tsvContent}
              onChange={(e) => setTsvContent(e.target.value)}
              onInput={(e) => setTsvContent(e.currentTarget.value)}
              onPaste={(e) => {
                // Sécurité anti-état-stale : re-synchronise après le paste natif
                setTimeout(() => {
                  setTsvContent(e.target.value);
                }, 0);
              }}
              placeholder={"libellé\tdescription\tunité\tprix\ttva"}
              className="border-slate-700 bg-slate-950/40 font-mono text-xs"
              data-testid="tsv-content"
            />
            {importResult && (
              <div className="rounded border border-slate-700 bg-slate-950/40 p-3 text-xs">
                <div className="text-slate-300">
                  Analysé : <b>{importResult.parsed}</b> ligne(s) · Importé :{" "}
                  <b className="text-emerald-400">{importResult.created}</b> · Doublons ignorés :{" "}
                  <b className="text-amber-400">{importResult.skipped_duplicates || 0}</b> · Erreurs :{" "}
                  <b className="text-red-400">{importResult.errors.length}</b>
                </div>
                {importResult.errors.length > 0 && (
                  <ul className="mt-2 max-h-40 overflow-y-auto text-red-300">
                    {importResult.errors.map((e, i) => (
                      <li key={i}>· {e.message}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => runImport(true)}
              disabled={importing || tsvContent.trim().length === 0}
              className="border-slate-700 bg-slate-800 text-slate-200"
              data-testid="preview-import-btn"
            >
              Prévisualiser
            </Button>
            <Button
              onClick={() => runImport(false)}
              disabled={importing || tsvContent.trim().length === 0}
              className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
              data-testid="confirm-import-btn"
            >
              {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmer l&apos;import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
